/**
 * AssigneeAvatarStack
 * Read-only stacked avatars for displaying multiple assignees.
 *
 * Props:
 *   assignees   {Array}   — [{ id, name }] or plain string array or mixed
 *   assignee    {string}  — fallback single assignee name (legacy)
 *   max         {number}  — max visible avatars before +N (default 3)
 *   size        {number}  — avatar diameter in px (default 24)
 *   colorResolver {function} — optional (assigneeObjOrName) => color
 *   showTooltip {boolean} — whether to show assignee names tooltip (default true)
 *   stackDirection {'row' | 'column'} — layout direction for the stack (default 'row')
 */
import React from 'react';
import { Avatar, Box, Tooltip } from '@mui/material';
import { COLORS } from './colors';
import { stringToColor } from './commonFunctions';


const AssigneeAvatarStack = ({
  assignees,
  assignee,
  max = 3,
  size = 24,
  colorResolver,
  showTooltip = true,
  stackDirection = 'row',
  onAvatarClick,
}) => {
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const [isScrolling, setIsScrolling] = React.useState(false);
  const scrollTimeoutRef = React.useRef(null);

  const list = React.useMemo(() => {
    if (Array.isArray(assignees) && assignees.length > 0) {
      return assignees
        .map((assigneeEntry) => {
          if (typeof assigneeEntry === 'object' && assigneeEntry) {
            const name = assigneeEntry.name || `${assigneeEntry.first_name || ''} ${assigneeEntry.last_name || ''}`.trim();
            return name ? { name, raw: assigneeEntry } : null;
          }
          if (typeof assigneeEntry === 'string' && assigneeEntry.trim()) {
            return { name: assigneeEntry.trim(), raw: assigneeEntry.trim() };
          }
          return null;
        })
        .filter(Boolean);
    }
    if (assignee) {
      if (typeof assignee === 'object') {
        const name = assignee.name || `${assignee.first_name || ''} ${assignee.last_name || ''}`.trim();
        return name ? [{ name, raw: assignee }] : [];
      }
      if (typeof assignee === 'string' && assignee.trim()) return [{ name: assignee.trim(), raw: assignee.trim() }];
    }
    return [];
  }, [assignees, assignee]);

  React.useEffect(() => {
    if (!showTooltip) return undefined;

    const handleScroll = () => {
      setTooltipOpen(false);
      setIsScrolling(true);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    const listenerOptions = { passive: true };

    window.addEventListener('wheel', handleScroll, listenerOptions);
    window.addEventListener('touchmove', handleScroll, listenerOptions);
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      window.removeEventListener('wheel', handleScroll, listenerOptions);
      window.removeEventListener('touchmove', handleScroll, listenerOptions);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [showTooltip]);

  if (list.length === 0) return null;

  const visible = list.slice(0, max);
  const safeVisible = Array.isArray(visible) ? visible : [];
  const overflow = list.length - max;
  const fontSize = `${Math.round(size * 0.42)}px`;

  const isColumn = stackDirection === 'column';

  const avatars = (
    <Box
      sx={{
        display: 'flex',
        alignItems: isColumn ? 'flex-start' : 'center',
        flexDirection: isColumn ? 'column' : 'row',
      }}
    >
      {safeVisible.map((entry, avatarIndex) => (
        <Avatar
          key={`${entry.name}-${avatarIndex}`}
          onClick={(e) => {
            if (onAvatarClick && entry.raw) {
              e.stopPropagation();
              onAvatarClick(entry.raw);
            }
          }}
          sx={{
            width: size, height: size, fontSize,
            bgcolor: colorResolver ? colorResolver(entry.raw || entry.name) : stringToColor(entry.name),
            fontWeight: 600,
            border: `2px solid ${COLORS.white}`,
            ml: isColumn ? 0 : (avatarIndex === 0 ? 0 : -0.75),
            mt: isColumn ? (avatarIndex === 0 ? 0 : -0.75) : 0,
            zIndex: safeVisible.length - avatarIndex,
            cursor: onAvatarClick && entry.raw ? 'pointer' : 'default',
          }}
        >
          {entry.name.charAt(0).toUpperCase()}
        </Avatar>
      ))}
      {overflow > 0 && (
        <Avatar
          sx={{
            width: size, height: size, fontSize: `${Math.round(size * 0.38)}px`,
            bgcolor: COLORS.gray?.[400],
            fontWeight: 700,
            border: `2px solid ${COLORS.white}`,
            ml: isColumn ? 0 : -0.75,
            mt: isColumn ? -0.75 : 0,
            zIndex: 0,
          }}
        >
          +{overflow}
        </Avatar>
      )}
    </Box>
  );

  if (!showTooltip) return avatars;

  const tooltipLabel = list
    .map((assigneeItem) => {
      const name = assigneeItem.name;
      if (assigneeItem.raw && typeof assigneeItem.raw === 'object' && String(assigneeItem.raw.status || '').toUpperCase() === 'SOFT-DELETE') {
        return `${name}(deleted)`;
      }
      return name;
    })
    .filter(Boolean)
    .join(', ');

  return (
    <Tooltip
      title={tooltipLabel}
      arrow
      open={tooltipOpen && !isScrolling}
      onOpen={() => {
        if (!isScrolling) {
          setTooltipOpen(true);
        }
      }}
      onClose={() => setTooltipOpen(false)}
      disableInteractive
      placement="top"
      TransitionProps={{ timeout: 0 }}
      slotProps={{
        tooltip: { className: 'app-tooltip' },
        popper: {
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                altBoundary: true,
                padding: 8,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom', 'top-start', 'bottom-start'],
                padding: 8,
              },
            },
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ],
        },
      }}
    >
      {avatars}
    </Tooltip>
  );
};

export default AssigneeAvatarStack;
